import { PAYMENT_STATES } from '@/utils/constants'
import debugLogger from '@/utils/debugLogger'
import { PAYMENT_TIMEOUT_MS } from '@/utils/paymentConstants'
import { useCallback, useEffect, useRef } from 'react'

/**
 * Hook for managing payment state transitions with timeout and modal logic.
 *
 * State Flow:
 *   IDLE -> WAITING_FOR_PAYMENT -> PAYMENT_SUCCESS -> IDLE
 *   IDLE -> WAITING_FOR_PAYMENT -> PAYMENT_ERROR -> IDLE
 *   IDLE -> SELECTED_CARD -> WAITING_FOR_PAYMENT -> ...
 *   WAITING_FOR_PAYMENT -> (timeout) -> IDLE
 */
export const usePaymentStateManager = ({
  paymentState,
  setPaymentState,
  setModalErrorIsOpen,
  isApiPending = false,
  onSuccess,
  onError,
  onIdle = () => {},
  timeoutMs = PAYMENT_TIMEOUT_MS,
}) => {
  const timeoutRef = useRef(null)
  const isMountedRef = useRef(true)
  const paymentStateRef = useRef(paymentState)
  const isApiPendingRef = useRef(isApiPending)

  // Keep refs in sync with state to avoid stale closures in setTimeout
  useEffect(() => {
    paymentStateRef.current = paymentState
  }, [paymentState])

  useEffect(() => {
    isApiPendingRef.current = isApiPending
  }, [isApiPending])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    debugLogger.log('State', 'Payment State Changed', paymentState)
  }, [paymentState])

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const resetToIdle = useCallback(() => {
    if (!isMountedRef.current) return

    setPaymentState({
      state: PAYMENT_STATES.IDLE,
      method: '',
      info: {},
    })
    onIdle()
  }, [setPaymentState, onIdle])

  // Handle PAYMENT_SUCCESS
  useEffect(() => {
    if (paymentState.state !== PAYMENT_STATES.PAYMENT_SUCCESS) return

    debugLogger.log('State', 'Payment success detected', paymentState.info)
    clearTimer()
    setModalErrorIsOpen(false)

    // Call onSuccess BEFORE resetting to IDLE so it can access paymentState.info
    onSuccess()

    // Reset to IDLE after callback completes
    resetToIdle()
  }, [
    paymentState.state,
    paymentState.info,
    clearTimer,
    setModalErrorIsOpen,
    onSuccess,
    resetToIdle,
  ])

  // Handle PAYMENT_ERROR
  useEffect(() => {
    if (paymentState.state !== PAYMENT_STATES.PAYMENT_ERROR) return

    debugLogger.log('State', 'Payment error detected', paymentState.info)
    clearTimer()
    setModalErrorIsOpen(PAYMENT_STATES.PAYMENT_ERROR)

    // Call onError BEFORE resetting so it can access error info
    onError()

    // Reset to IDLE after callback
    resetToIdle()
  }, [
    paymentState.state,
    paymentState.info,
    clearTimer,
    setModalErrorIsOpen,
    onError,
    resetToIdle,
  ])

  // Handle WAITING_FOR_PAYMENT with timeout
  useEffect(() => {
    if (paymentState.state !== PAYMENT_STATES.WAITING_FOR_PAYMENT) return

    debugLogger.log('State', 'Waiting for payment - setting timeout', timeoutMs)
    clearTimer()

    timeoutRef.current = setTimeout(() => {
      if (!isMountedRef.current) return

      // Use ref to get current state (avoid stale closure)
      const currentState = paymentStateRef.current.state
      if (currentState !== PAYMENT_STATES.WAITING_FOR_PAYMENT) {
        debugLogger.log('State', 'State changed during timeout, skipping reset')
        return
      }

      if (isApiPendingRef.current) {
        debugLogger.log(
          'State',
          'API is still pending at timeout, skipping reset'
        )
        return
      }

      debugLogger.warn('State', 'Timeout triggered, resetting to IDLE')
      setModalErrorIsOpen(false)
      resetToIdle()
      timeoutRef.current = null
    }, timeoutMs)

    setModalErrorIsOpen(PAYMENT_STATES.WAITING_FOR_PAYMENT)

    return () => clearTimer()
  }, [
    paymentState.state,
    timeoutMs,
    clearTimer,
    resetToIdle,
    setModalErrorIsOpen,
  ])

  // Handle SELECTED_CARD (UI-only state, just log it)
  useEffect(() => {
    if (paymentState.state !== PAYMENT_STATES.SELECTED_CARD) return
    debugLogger.log('State', 'Card selected, awaiting payment submission')
  }, [paymentState.state])

  return { clearTimer, resetToIdle }
}

export default usePaymentStateManager
